import { useState, useEffect, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type {
  ClassCategoryRow,
  ClassCategoryInsert,
  SubClassRow,
  SubClassInsert,
} from "@/stores/admin/models";

// ---------------------------------------------------------------------------
// Inline-edit helpers
// ---------------------------------------------------------------------------

interface CategoryEdit {
  class_category: string;
}

interface SubClassEdit {
  sub_class: string;
  sub_class_second: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ClassHierarchyTable = observer(() => {
  const { classHierarchyStore } = useStores();

  // ----- Categories state (local UI) -----
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [categoryEdits, setCategoryEdits] = useState<Record<number, CategoryEdit>>({});
  const [savingCategory, setSavingCategory] = useState<number | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<number | null>(null);

  // Add category dialog
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [newCategoryId, setNewCategoryId] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Delete category confirm
  const [categoryDeleteConfirmOpen, setCategoryDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<ClassCategoryRow | null>(null);

  // ----- Sub-classes state (local UI) -----
  const [subClassEdits, setSubClassEdits] = useState<Record<number, SubClassEdit>>({});
  const [savingSubClass, setSavingSubClass] = useState<number | null>(null);
  const [deletingSubClass, setDeletingSubClass] = useState<number | null>(null);

  // Add sub-class dialog
  const [addSubClassOpen, setAddSubClassOpen] = useState(false);
  const [newSubClassName, setNewSubClassName] = useState("");
  const [newSubClassSecond, setNewSubClassSecond] = useState("");
  const [creatingSubClass, setCreatingSubClass] = useState(false);

  // Delete sub-class confirm
  const [subClassDeleteConfirmOpen, setSubClassDeleteConfirmOpen] = useState(false);
  const [subClassToDelete, setSubClassToDelete] = useState<SubClassRow | null>(null);

  const categories = classHierarchyStore.categoriesList;
  // Track the underlying MST map entry so identity is stable across renders
  // unless the bucket is replaced (on loadSubClasses). Length is included so
  // add/delete still triggers edit-state resync.
  const subClassEntry =
    selectedCategoryId !== null
      ? classHierarchyStore.subClassesByCategory.get(String(selectedCategoryId))
      : undefined;
  const subClassEntryLength = subClassEntry?.length ?? 0;
  const subClasses = useMemo(
    () => (subClassEntry ? (subClassEntry.slice() as unknown as SubClassRow[]) : []),
    [subClassEntry, subClassEntryLength]
  );

  // =========================================================================
  // Load categories (once)
  // =========================================================================

  useEffect(() => {
    void classHierarchyStore.loadClassCategories().catch(() => {
      toast.error("Failed to load class categories");
    });
  }, [classHierarchyStore]);

  // Keep category edit state in sync with store
  useEffect(() => {
    const edits: Record<number, CategoryEdit> = {};
    categories.forEach((c) => {
      edits[c.category_id] = { class_category: c.class_category };
    });
    setCategoryEdits(edits);
  }, [categories]);

  // =========================================================================
  // Load sub-classes for selected category
  // =========================================================================

  useEffect(() => {
    if (selectedCategoryId !== null) {
      void classHierarchyStore.loadSubClasses(selectedCategoryId).catch(() => {
        toast.error("Failed to load sub-classes");
      });
    }
  }, [selectedCategoryId, classHierarchyStore]);

  // Keep sub-class edit state in sync with store
  useEffect(() => {
    const edits: Record<number, SubClassEdit> = {};
    subClasses.forEach((sc) => {
      edits[sc.id] = {
        sub_class: sc.sub_class,
        sub_class_second: sc.sub_class_second,
      };
    });
    setSubClassEdits(edits);
  }, [subClasses]);

  // =========================================================================
  // Category CRUD
  // =========================================================================

  const handleCreateCategory = async () => {
    const id = Number(newCategoryId);
    if (!id || !newCategoryName.trim()) return;

    setCreatingCategory(true);
    try {
      const input: ClassCategoryInsert = {
        category_id: id,
        class_category: newCategoryName.trim(),
      };
      await classHierarchyStore.createClassCategory(input);
      toast.success("Category created");
      setAddCategoryOpen(false);
      setNewCategoryId("");
      setNewCategoryName("");
    } catch {
      toast.error("Failed to create category");
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleSaveCategory = async (categoryId: number) => {
    const edit = categoryEdits[categoryId];
    if (!edit) return;

    setSavingCategory(categoryId);
    try {
      await classHierarchyStore.updateClassCategory(categoryId, {
        class_category: edit.class_category,
      });
      toast.success("Category updated");
    } catch {
      toast.error("Failed to update category");
    } finally {
      setSavingCategory(null);
    }
  };

  const handleDeleteCategoryClick = useCallback((cat: ClassCategoryRow) => {
    setCategoryToDelete(cat);
    setCategoryDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDeleteCategory = useCallback(async () => {
    if (!categoryToDelete) return;

    setDeletingCategory(categoryToDelete.category_id);
    try {
      await classHierarchyStore.deleteClassCategory(categoryToDelete.category_id);
      toast.success("Category deleted");
      if (selectedCategoryId === categoryToDelete.category_id) {
        setSelectedCategoryId(null);
      }
    } catch {
      toast.error("Failed to delete category");
    } finally {
      setDeletingCategory(null);
      setCategoryDeleteConfirmOpen(false);
      setCategoryToDelete(null);
    }
  }, [categoryToDelete, selectedCategoryId, classHierarchyStore]);

  // =========================================================================
  // Sub-class CRUD
  // =========================================================================

  const handleCreateSubClass = async () => {
    if (!newSubClassName.trim() || selectedCategoryId === null) return;

    setCreatingSubClass(true);
    try {
      const input: SubClassInsert = {
        sub_class: newSubClassName.trim(),
        sub_class_second: newSubClassSecond.trim() || null,
        category_id: selectedCategoryId,
      };
      await classHierarchyStore.createSubClass(input);
      toast.success("Sub-class created");
      setAddSubClassOpen(false);
      setNewSubClassName("");
      setNewSubClassSecond("");
    } catch {
      toast.error("Failed to create sub-class");
    } finally {
      setCreatingSubClass(false);
    }
  };

  const handleSaveSubClass = async (id: number) => {
    const edit = subClassEdits[id];
    if (!edit || selectedCategoryId === null) return;

    setSavingSubClass(id);
    try {
      await classHierarchyStore.updateSubClass(id, {
        sub_class: edit.sub_class,
        sub_class_second: edit.sub_class_second,
      });
      toast.success("Sub-class updated");
    } catch {
      toast.error("Failed to update sub-class");
    } finally {
      setSavingSubClass(null);
    }
  };

  const handleDeleteSubClassClick = useCallback((sc: SubClassRow) => {
    setSubClassToDelete(sc);
    setSubClassDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDeleteSubClass = useCallback(async () => {
    if (!subClassToDelete || selectedCategoryId === null) return;

    setDeletingSubClass(subClassToDelete.id);
    try {
      await classHierarchyStore.deleteSubClass(subClassToDelete.id);
      toast.success("Sub-class deleted");
    } catch {
      toast.error("Failed to delete sub-class");
    } finally {
      setDeletingSubClass(null);
      setSubClassDeleteConfirmOpen(false);
      setSubClassToDelete(null);
    }
  }, [subClassToDelete, selectedCategoryId, classHierarchyStore]);

  // =========================================================================
  // Field change helpers
  // =========================================================================

  const handleCategoryFieldChange = (categoryId: number, value: string) => {
    setCategoryEdits((prev) => ({
      ...prev,
      [categoryId]: { ...prev[categoryId], class_category: value },
    }));
  };

  const handleSubClassFieldChange = (
    id: number,
    field: keyof SubClassEdit,
    value: string | number | null
  ) => {
    setSubClassEdits((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  // =========================================================================
  // Render
  // =========================================================================

  if (classHierarchyStore.isLoading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex gap-4">
      {/* ------------------------------------------------------------------ */}
      {/* Left panel: Class Categories (40%) */}
      {/* ------------------------------------------------------------------ */}
      <Card className="w-2/5">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-base font-semibold">Class Categories</CardTitle>
          <Button size="sm" onClick={() => setAddCategoryOpen(true)} className="gap-1">
            <Plus className="h-4 w-4" />
            Add Category
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">ID</TableHead>
                <TableHead>Category Name</TableHead>
                <TableHead className="w-[140px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((cat) => (
                <TableRow
                  key={cat.category_id}
                  className={`cursor-pointer ${selectedCategoryId === cat.category_id ? "bg-muted" : ""}`}
                  onClick={() => setSelectedCategoryId(cat.category_id)}
                >
                  <TableCell className="font-medium">{cat.category_id}</TableCell>
                  <TableCell>
                    <Input
                      value={categoryEdits[cat.category_id]?.class_category ?? ""}
                      onChange={(e) => handleCategoryFieldChange(cat.category_id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-8"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSaveCategory(cat.category_id)}
                        disabled={savingCategory === cat.category_id}
                      >
                        {savingCategory === cat.category_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Pencil className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteCategoryClick(cat)}
                        disabled={deletingCategory === cat.category_id}
                      >
                        {deletingCategory === cat.category_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {categories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground">
                    No categories found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Right panel: Sub-Classes (60%) */}
      {/* ------------------------------------------------------------------ */}
      <Card className="w-3/5">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-base font-semibold">
            {selectedCategoryId !== null
              ? `Sub-Classes - ${categories.find((c) => c.category_id === selectedCategoryId)?.class_category ?? ""}`
              : "Sub-Classes"}
          </CardTitle>
          {selectedCategoryId !== null && (
            <Button size="sm" onClick={() => setAddSubClassOpen(true)} className="gap-1">
              <Plus className="h-4 w-4" />
              Add Sub-Class
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {selectedCategoryId === null ? (
            <p className="py-8 text-center text-muted-foreground">
              Select a category to view its sub-classes
            </p>
          ) : classHierarchyStore.isLoadingSubClasses && subClasses.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">ID</TableHead>
                  <TableHead>Sub-Class</TableHead>
                  <TableHead>Secondary Name</TableHead>
                  <TableHead className="w-[140px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subClasses.map((sc) => (
                  <TableRow key={sc.id}>
                    <TableCell className="font-medium">{sc.id}</TableCell>
                    <TableCell>
                      <Input
                        value={subClassEdits[sc.id]?.sub_class ?? ""}
                        onChange={(e) =>
                          handleSubClassFieldChange(sc.id, "sub_class", e.target.value)
                        }
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={subClassEdits[sc.id]?.sub_class_second ?? ""}
                        onChange={(e) =>
                          handleSubClassFieldChange(
                            sc.id,
                            "sub_class_second",
                            e.target.value || null
                          )
                        }
                        placeholder="Optional"
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSaveSubClass(sc.id)}
                          disabled={savingSubClass === sc.id}
                        >
                          {savingSubClass === sc.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Pencil className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteSubClassClick(sc)}
                          disabled={deletingSubClass === sc.id}
                        >
                          {deletingSubClass === sc.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {subClasses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      No sub-classes in this category
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ================================================================== */}
      {/* Add Category Dialog */}
      {/* ================================================================== */}
      <Dialog open={addCategoryOpen} onOpenChange={setAddCategoryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Class Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-cat-id">Category ID</Label>
              <Input
                id="new-cat-id"
                type="number"
                value={newCategoryId}
                onChange={(e) => setNewCategoryId(e.target.value)}
                placeholder="e.g. 5"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-cat-name">Category Name</Label>
              <Input
                id="new-cat-name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="e.g. Space"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCategoryOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateCategory}
              disabled={creatingCategory || !newCategoryId || !newCategoryName.trim()}
            >
              {creatingCategory ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* Add Sub-Class Dialog */}
      {/* ================================================================== */}
      <Dialog open={addSubClassOpen} onOpenChange={setAddSubClassOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Sub-Class</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-sc-name">Sub-Class Name</Label>
              <Input
                id="new-sc-name"
                value={newSubClassName}
                onChange={(e) => setNewSubClassName(e.target.value)}
                placeholder="e.g. Building"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-sc-second">Secondary Name (optional)</Label>
              <Input
                id="new-sc-second"
                value={newSubClassSecond}
                onChange={(e) => setNewSubClassSecond(e.target.value)}
                placeholder="e.g. Hoone"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddSubClassOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubClass}
              disabled={creatingSubClass || !newSubClassName.trim()}
            >
              {creatingSubClass ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* Confirm Dialogs */}
      {/* ================================================================== */}
      <ConfirmDialog
        open={categoryDeleteConfirmOpen}
        onOpenChange={setCategoryDeleteConfirmOpen}
        onConfirm={handleConfirmDeleteCategory}
        title="Delete Class Category"
        description={`Are you sure you want to delete "${categoryToDelete?.class_category}"? All sub-classes in this category will also be deleted. This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
      />

      <ConfirmDialog
        open={subClassDeleteConfirmOpen}
        onOpenChange={setSubClassDeleteConfirmOpen}
        onConfirm={handleConfirmDeleteSubClass}
        title="Delete Sub-Class"
        description={`Are you sure you want to delete "${subClassToDelete?.sub_class}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
      />
    </div>
  );
});

export default ClassHierarchyTable;
